import { useAppTheme } from '@/shared/theme';
import { SxProps } from '@mui/system';
import { Theme } from '@mui/material';
import { useSimpleSearchTheme } from '@/shared/ui/search/SimpleSearch/theme/useSimpleSearchTheme';

export function useAdvancedSearchTheme() {
  const { theme } = useAppTheme();
  const tokens = theme.advancedSearch;

  const containerSx: SxProps<Theme> = {
    background: tokens.container.background,
    border: `1px solid ${tokens.container.border}`,
    borderRadius: tokens.container.borderRadius,
    padding: tokens.container.padding,
    fontFamily: tokens.input.fontFamily,
  };

  const filterGroupCardSx: SxProps<Theme> = {
    background: tokens.filterGroup.background,
    border: `1px solid ${tokens.filterGroup.border}`,
    borderRadius: tokens.filterGroup.borderRadius,
    padding: tokens.filterGroup.padding,
    margin: tokens.filterGroup.margin,
  };

  const filterGroupHeaderSx: SxProps<Theme> = {
    background: tokens.filterGroup.header.background,
    padding: tokens.filterGroup.header.padding,
    fontSize: tokens.filterGroup.header.fontSize,
    fontWeight: tokens.filterGroup.header.fontWeight,
    fontFamily: tokens.filterGroup.header.fontFamily,
    color: tokens.filterGroup.header.color,
    borderRadius: '4px 4px 0 0',
    marginBottom: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  };

  const filterGroupHeaderTextSx: SxProps<Theme> = {
    fontSize: tokens.filterGroup.header.fontSize,
    fontWeight: tokens.filterGroup.header.fontWeight,
    fontFamily: tokens.filterGroup.header.fontFamily,
    color: tokens.filterGroup.header.color,
  };

  const getLogicBadgeSx = (logic: 'AND' | 'OR'): SxProps<Theme> => ({
    background:
      logic === 'AND'
        ? tokens.filterGroup.logicBadge.background.and
        : tokens.filterGroup.logicBadge.background.or,
    color: tokens.filterGroup.logicBadge.color,
    padding: tokens.filterGroup.logicBadge.padding,
    fontSize: tokens.filterGroup.logicBadge.fontSize,
    borderRadius: '4px',
    fontWeight: 600,
  });

  const filterRowSx: SxProps<Theme> = {
    display: 'flex',
    gap: tokens.filterRow.gap,
    padding: tokens.filterRow.padding,
    background: tokens.filterRow.background,
    borderRadius: tokens.filterRow.borderRadius,
    alignItems: 'center',
    transition: 'background 0.2s ease',
    '&:hover': {
      background: tokens.filterRow.hover,
    },
  };

  const fieldSelectSx: SxProps<Theme> = {
    minWidth: tokens.input.field.minWidth,
    fontSize: tokens.input.fontSize,
    fontFamily: tokens.input.fontFamily,
  };

  const operatorSelectSx: SxProps<Theme> = {
    minWidth: tokens.input.operator.minWidth,
    fontSize: tokens.input.fontSize,
    fontFamily: tokens.input.fontFamily,
  };

  const valueInputSx: SxProps<Theme> = {
    minWidth: tokens.input.value.minWidth,
    fontSize: tokens.input.fontSize,
    fontFamily: tokens.input.fontFamily,
  };

  const getPrimaryButtonSx = (): SxProps<Theme> => ({
    fontSize: tokens.button.fontSize,
    fontWeight: tokens.button.fontWeight,
    fontFamily: tokens.button.fontFamily,
    background: tokens.button.primary.background,
    color: tokens.button.primary.color,
    '&:hover': {
      background: tokens.button.primary.hover,
    },
  });

  const getSecondaryButtonSx = (): SxProps<Theme> => ({
    fontSize: tokens.button.fontSize,
    fontWeight: tokens.button.fontWeight,
    fontFamily: tokens.button.fontFamily,
    background: tokens.button.secondary.background,
    color: tokens.button.secondary.color,
    '&:hover': {
      background: tokens.button.secondary.hover,
    },
  });

  const getDangerButtonSx = (): SxProps<Theme> => ({
    fontSize: tokens.button.fontSize,
    fontWeight: tokens.button.fontWeight,
    fontFamily: tokens.button.fontFamily,
    background: tokens.button.danger.background,
    color: tokens.button.danger.color,
    '&:hover': {
      background: tokens.button.danger.hover,
    },
  });

  const toolbarSx: SxProps<Theme> = {
    background: tokens.toolbar.background,
    borderBottom: tokens.toolbar.borderBottom,
    padding: tokens.toolbar.padding,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: tokens.toolbar.gap,
  };

  const sortControlSx: SxProps<Theme> = {
    background: tokens.sortControl.background,
    border: `1px solid ${tokens.sortControl.border}`,
    borderRadius: tokens.sortControl.borderRadius,
    padding: tokens.sortControl.padding,
    display: 'flex',
    alignItems: 'center',
    gap: tokens.sortControl.gap,
  };

  const sortLabelSx: SxProps<Theme> = {
    fontSize: tokens.sortControl.label.fontSize,
    fontWeight: tokens.sortControl.label.fontWeight,
    color: tokens.sortControl.label.color,
  };

  const simpleSearchTheme = useSimpleSearchTheme();
  const searchContainerSx: SxProps<Theme> = {
    ...simpleSearchTheme.containerSx,
    display: 'block', // Stack을 사용하므로 flex가 아닌 block
  };

  const addFilterButtonSx: SxProps<Theme> = {
    fontSize: tokens.button.fontSize,
    fontWeight: tokens.button.fontWeight,
    fontFamily: tokens.button.fontFamily,
    color: tokens.button.secondary.color,
    textTransform: 'none',
    '&:hover': {
      background: tokens.button.secondary.hover,
    },
  };

  const addGroupButtonSx: SxProps<Theme> = {
    fontSize: tokens.button.fontSize,
    fontWeight: tokens.button.fontWeight,
    fontFamily: tokens.button.fontFamily,
    textTransform: 'none',
  };

  const searchButtonSx: SxProps<Theme> = {
    ...simpleSearchTheme.searchButtonSx,
  };

  return {
    tokens,
    containerSx,
    filterGroupCardSx,
    filterGroupHeaderSx,
    filterGroupHeaderTextSx,
    getLogicBadgeSx,
    filterRowSx,
    fieldSelectSx,
    operatorSelectSx,
    valueInputSx,
    getPrimaryButtonSx,
    getSecondaryButtonSx,
    getDangerButtonSx,
    toolbarSx,
    sortControlSx,
    sortLabelSx,
    searchContainerSx,
    addFilterButtonSx,
    addGroupButtonSx,
    searchButtonSx,
  };
}
